/**
 * Select — custom glass dropdown (LHUI).
 *
 * Native <select> popup má ostré hrany a systémové barvy → nesedí ke sklu.
 * Sbalený trigger = FIELD_SURFACE; rozbalené menu = acm-menu (zaoblené sklo).
 *
 * API kompatibilní s dřívějším <select>: value / onChange(e.target.value),
 * options[] nebo children <option>, size sm|md, placeholder, disabled.
 */
import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FIELD_SURFACE } from './Input';

export type SelectOption = { value: string; label: string; disabled?: boolean };

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: 'sm' | 'md';
  /** Ploché volby. Alternativa k `children` (pro dynamické <option>). */
  options?: Array<SelectOption> | string[];
  placeholder?: string;
  /** Pole nad seznamem. Dlouhé seznamy (skupiny, třídy) se bez něj nedají dohledat. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

function labelOf(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(labelOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return labelOf(node.props.children);
  return '';
}

/** Vyčti <option> / <optgroup> children do plochého seznamu. */
function optionsFromChildren(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = [];
  // Children.forEach snese null/undefined; neplatné tvary (plain object) ošetři try.
  try {
    Children.forEach(children, (child) => {
      if (!isValidElement<{ value?: string | number; disabled?: boolean; children?: ReactNode; label?: string }>(child)) {
        return;
      }
      const t = child.type;
      if (t === 'optgroup') {
        Children.forEach(child.props.children, (sub) => {
          if (!isValidElement<{ value?: string | number; disabled?: boolean; children?: ReactNode }>(sub)) return;
          if (sub.type !== 'option') return;
          const label = labelOf(sub.props.children);
          const value = sub.props.value !== undefined ? String(sub.props.value) : label;
          out.push({ value, label, disabled: !!sub.props.disabled });
        });
        return;
      }
      if (t === 'option') {
        const label = labelOf(child.props.children);
        const value = child.props.value !== undefined ? String(child.props.value) : label;
        out.push({ value, label, disabled: !!child.props.disabled });
      }
    });
  } catch {
    /* ignore malformed children */
  }
  return out;
}

function normalizeOptions(
  options: SelectProps['options'],
  children: ReactNode,
): SelectOption[] {
  if (options != null) {
    if (!Array.isArray(options)) return [];
    return options.map((opt) =>
      typeof opt === 'string'
        ? { value: opt, label: opt }
        : { value: String(opt?.value ?? ''), label: String(opt?.label ?? opt?.value ?? ''), disabled: !!opt?.disabled },
    );
  }
  return optionsFromChildren(children);
}

type MenuPos = { top: number; left: number; width: number; maxH: number; openUp: boolean };

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      className,
      size = 'md',
      options,
      placeholder,
      children,
      value,
      defaultValue,
      onChange,
      disabled,
      name,
      id,
      'aria-label': ariaLabel,
      searchable = false,
      searchPlaceholder,
      ...rest
    },
    ref,
  ) => {
    const list = useMemo(() => normalizeOptions(options, children), [options, children]);
    const [q, setQ] = useState('');
    const needle = searchable ? q.trim().toLowerCase() : '';
    const visible = useMemo(() => {
      if (!needle) return list;
      return list.filter((opt) =>
        opt.label.toLowerCase().includes(needle) || opt.value.toLowerCase().includes(needle));
    }, [list, needle]);
    const isControlled = value !== undefined;
    const [inner, setInner] = useState(() =>
      defaultValue !== undefined ? String(defaultValue) : '',
    );
    const current = isControlled ? String(value ?? '') : inner;

    const [open, setOpen] = useState(false);
    const [hi, setHi] = useState(-1);
    const [pos, setPos] = useState<MenuPos | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const listId = useId();

    const setBtnRef = useCallback(
      (el: HTMLButtonElement | null) => {
        btnRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as MutableRefObject<HTMLButtonElement | null>).current = el;
      },
      [ref],
    );

    const selected = list.find((o) => o.value === current);
    const display = selected?.label
      ?? (placeholder && !current ? placeholder : current || placeholder || '—');

    const fireChange = useCallback(
      (next: string) => {
        if (!isControlled) setInner(next);
        if (!onChange) return;
        const fake = {
          target: { value: next, name: name ?? '' },
          currentTarget: { value: next, name: name ?? '' },
        } as ChangeEvent<HTMLSelectElement>;
        onChange(fake);
      },
      [isControlled, name, onChange],
    );

    const place = useCallback(() => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gap = 6;
      const edge = 8; // odsadit od okraje viewportu
      const spaceBelow = window.innerHeight - r.bottom - gap;
      const spaceAbove = r.top - gap;
      const preferDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;
      const maxH = Math.min(280, Math.max(120, preferDown ? spaceBelow - 8 : spaceAbove - 8));
      const width = Math.min(Math.max(r.width, 140), window.innerWidth - edge * 2);
      let left = r.left;
      if (left + width > window.innerWidth - edge) {
        left = Math.max(edge, window.innerWidth - edge - width);
      }
      if (left < edge) left = edge;
      setPos({
        top: preferDown ? r.bottom + gap : r.top - gap,
        left,
        width,
        maxH,
        openUp: !preferDown,
      });
    }, []);

    const close = useCallback(() => {
      setOpen(false);
      setHi(-1);
      setQ('');
    }, []);

    const openMenu = useCallback(() => {
      if (disabled) return;
      place();
      setQ('');
      const idx = Math.max(0, list.findIndex((o) => o.value === current && !o.disabled));
      setHi(idx >= 0 ? idx : list.findIndex((o) => !o.disabled));
      setOpen(true);
    }, [current, disabled, list, place]);

    useLayoutEffect(() => {
      if (!open) return;
      place();
      const onScroll = () => place();
      window.addEventListener('resize', place);
      // capture scroll on any parent
      window.addEventListener('scroll', onScroll, true);
      return () => {
        window.removeEventListener('resize', place);
        window.removeEventListener('scroll', onScroll, true);
      };
    }, [open, place]);

    useEffect(() => {
      if (!open) return;
      const onDoc = (e: MouseEvent) => {
        const t = e.target as Node;
        if (rootRef.current?.contains(t)) return;
        if (menuRef.current?.contains(t)) return;
        close();
      };
      document.addEventListener('mousedown', onDoc);
      return () => document.removeEventListener('mousedown', onDoc);
    }, [open, close]);

    useEffect(() => {
      if (!open || hi < 0) return;
      const node = menuRef.current?.querySelector<HTMLElement>(`[data-idx="${hi}"]`);
      node?.scrollIntoView({ block: 'nearest' });
    }, [hi, open]);

    const pick = (opt: SelectOption) => {
      if (opt.disabled) return;
      fireChange(opt.value);
      close();
      btnRef.current?.focus();
    };

    const moveHi = (dir: 1 | -1) => {
      if (visible.length === 0) return;
      let i = hi;
      for (let n = 0; n < visible.length; n++) {
        i = (i + dir + visible.length) % visible.length;
        if (!visible[i]?.disabled) {
          setHi(i);
          return;
        }
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (disabled) return;
      if (!open) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openMenu();
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveHi(1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveHi(-1);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const opt = visible[hi];
        if (opt) pick(opt);
        return;
      }
      if (e.key === 'Tab') close();
    };

    const sizes = {
      sm: 'h-8 pl-3 pr-8 text-xs',
      md: 'h-10 pl-4 pr-9 text-sm',
    };
    const chevron = size === 'sm' ? 12 : 14;
    const isPlaceholder = !selected && !!placeholder;

    // strip native-only rest that shouldn't hit the button
    void rest;

    const menu = open && pos && typeof document !== 'undefined'
      ? createPortal(
        <div
          ref={menuRef}
          id={listId}
          role='listbox'
          aria-activedescendant={hi >= 0 ? `${listId}-opt-${hi}` : undefined}
          className={cn(
            // Menu je portálované do body. Musí ležet nad dialogy (MCP connect
            // používá z-[90]); jinak se otevře správně, ale zůstane pod modalem.
            'acm-menu fixed z-[110] overflow-hidden animate-fade-in',
            'rounded-xl p-1',
          )}
          style={{
            top: pos.openUp ? undefined : pos.top,
            bottom: pos.openUp ? window.innerHeight - pos.top : undefined,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxH,
          }}
        >
          {searchable && (
            <div className='p-1'>
              <input
                autoFocus
                value={q}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(event) => {
                  setQ(event.target.value);
                  setHi(0);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    moveHi(1);
                  } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    moveHi(-1);
                  } else if (event.key === 'Enter') {
                    event.preventDefault();
                    const opt = visible[hi];
                    if (opt) pick(opt);
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    close();
                    btnRef.current?.focus();
                  }
                }}
                className={cn('h-8 w-full rounded-lg px-3 text-xs', FIELD_SURFACE)}
              />
            </div>
          )}
          <div className='overflow-y-auto overscroll-contain' style={{ maxHeight: pos.maxH - (searchable ? 48 : 8) }}>
            {visible.length === 0 && (
              <div className='px-3 py-2 text-xs text-gray-500'>—</div>
            )}
            {visible.map((opt, i) => {
              const active = opt.value === current;
              const focused = i === hi;
              return (
                <div
                  key={`${opt.value}-${i}`}
                  id={`${listId}-opt-${i}`}
                  data-idx={i}
                  role='option'
                  aria-selected={active}
                  aria-disabled={opt.disabled || undefined}
                  className={cn(
                    'acm-menu-item flex items-center gap-2 w-full text-left',
                    'rounded-lg px-3 py-2 cursor-pointer transition-colors',
                    size === 'sm' ? 'text-xs' : 'text-sm',
                    opt.disabled && 'opacity-40 pointer-events-none',
                    active && 'bg-plex/15 text-plex font-medium',
                    !active && focused && 'bg-gray-800/70 text-gray-100',
                    !active && !focused && 'text-gray-200 hover:bg-gray-800/50 hover:text-gray-100',
                  )}
                  onMouseEnter={() => setHi(i)}
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    pick(opt);
                  }}
                >
                  <span className='flex-1 truncate'>{opt.label}</span>
                  {active && <Check size={14} className='shrink-0 text-plex' />}
                </div>
              );
            })}
          </div>
        </div>,
        document.body,
      )
      : null;

    return (
      <div ref={rootRef} className={cn('relative', className)}>
        {name != null && <input type='hidden' name={name} value={current} />}
        <button
          type='button'
          ref={setBtnRef}
          id={id}
          disabled={disabled}
          aria-haspopup='listbox'
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={ariaLabel}
          className={cn(
            'relative flex w-full items-center rounded-lg text-left appearance-none',
            FIELD_SURFACE,
            sizes[size],
            open && 'border-plex/50',
            isPlaceholder && 'text-gray-500',
          )}
          onClick={() => (open ? close() : openMenu())}
          onKeyDown={onKeyDown}
        >
          <span className='flex-1 truncate pr-1'>{display}</span>
          <ChevronDown
            size={chevron}
            className={cn(
              'absolute right-2.5 text-gray-500 transition-transform duration-150 pointer-events-none',
              open && 'rotate-180 text-plex',
            )}
          />
        </button>
        {menu}
      </div>
    );
  },
);
Select.displayName = 'Select';
